import type { SorteDeLien } from '@nextilt/commun';
import type { Liens } from '../partage/api/types';

/*
 * **« Lier », la règle du clic** (chantier liens-entre-bulles, ticket 04, décisions 2, 3 et 11 du
 * cadrage) : on clique la première bulle, on choisit la sorte, puis chaque bulle cliquée est
 * reliée à la première. Pure : l'écran (`Lier.tsx`) la lit à chaque clic, le serveur pose.
 *
 * Ce qui reste au serveur, et ne se refait pas ici : un lien par paire (la pose change le lien
 * existant), le même lien refusé (`lien_deja_pose`), le refus de boucle d'un « attend ». Ce qui
 * est propre à « Lier » : le sens va de la première vers les bulles cliquées, et **rien ne se
 * retourne** (décision 11 du cadrage) : une paire déjà liée dans l'autre sens renvoie à ⇄.
 */

/** Les bouts du lien qu'on pose : un « attend » va de la bulle attendue vers celle qui attend. */
export interface BoutsDuLien {
  sourceId: string;
  cibleId: string;
}

/**
 * Les bouts d'un lien de la première vers la bulle cliquée : « La première attend… » (la cliquée
 * est attendue, donc la source), « La première sert à… » et « touche à… » (la première, source).
 */
export function boutsDuLien(premiere: string, cliquee: string, sorte: SorteDeLien): BoutsDuLien {
  return sorte === 'attend' ? { sourceId: cliquee, cibleId: premiere } : { sourceId: premiere, cibleId: cliquee };
}

/** Le lien qui relie déjà deux bulles : sa sorte et ses bouts. */
export interface LienDeLaPaire extends BoutsDuLien {
  sorte: SorteDeLien;
}

/**
 * Le lien qui relie déjà la première à la bulle cliquée, lu dans les liens de la première tels que
 * le serveur les rend (`GET /api/dependances/:id`) : ce qu'elle attend, ce qui l'attend, ses autres
 * liens avec leur sens vu d'elle. `null` : aucun.
 */
export function lienDeLaPaire(premiere: string, liens: Liens, cliquee: string): LienDeLaPaire | null {
  if (liens.attend.some((l) => l.id === cliquee)) return { sorte: 'attend', sourceId: cliquee, cibleId: premiere };
  if (liens.bloque.some((l) => l.id === cliquee)) return { sorte: 'attend', sourceId: premiere, cibleId: cliquee };
  const autre = liens.autresLiens.find((l) => l.id === cliquee);
  if (!autre) return null;
  return autre.sens === 'entrant'
    ? { sorte: autre.sorte, sourceId: cliquee, cibleId: premiere }
    : { sorte: autre.sorte, sourceId: premiere, cibleId: cliquee };
}

/** Ce que fait un clic sur une bulle, pendant « Lier ». */
export type IssueDuClic =
  /** Elle devient la première (aucune encore, ou la sorte pas encore choisie : on se reprend). */
  | { sorte: 'premiere'; id: string }
  /** La première elle-même : une bulle ne se lie pas à elle-même. */
  | { sorte: 'soi' }
  /** Les liens de la première ne sont pas encore lus : rien ne part, pour ne rien retourner. */
  | { sorte: 'patiente' }
  /** La paire est liée dans l'autre sens : rien ne se retourne, ⇄ sur le trait le fait. */
  | { sorte: 'autre_sens' }
  /** On pose : un lien neuf, ou le lien de la paire qui change de sorte (le serveur en juge). */
  | ({ sorte: 'poser'; lien: SorteDeLien } & BoutsDuLien);

/** Où en est « Lier » : la première bulle, et la sorte choisie. */
export interface EtatDeLier {
  premiere: string | null;
  sorte: SorteDeLien | null;
}

/**
 * Le clic sur `cliquee`, avec les liens de la première (`undefined` : pas encore lus). Une paire
 * déjà liée change de sorte, sauf si le lien voulu irait dans l'autre sens que celui qui est posé ;
 * un « touche à », sans sens, se change en tout, et tout se change en lui.
 */
export function issueDuClic(etat: EtatDeLier, cliquee: string, liens: Liens | undefined): IssueDuClic {
  const { premiere, sorte } = etat;
  if (premiere === null || sorte === null) return { sorte: 'premiere', id: cliquee };
  if (cliquee === premiere) return { sorte: 'soi' };
  if (!liens) return { sorte: 'patiente' };
  const voulu = boutsDuLien(premiere, cliquee, sorte);
  const pose = lienDeLaPaire(premiere, liens, cliquee);
  if (pose && pose.sorte !== 'touche_a' && sorte !== 'touche_a' && pose.sourceId !== voulu.sourceId) {
    return { sorte: 'autre_sens' };
  }
  return { sorte: 'poser', lien: sorte, ...voulu };
}
